import React from 'react';
import styled from 'styled-components';

import { ONION_OPACITY } from '../../constants';
import { marcTransformToCss } from '../../virtualCoords';

const HANDLE_SIZE = 14;
const ROTATE_OFFSET = 28;

function OnionMarc({
  src,
  visible,
  opacity,
  transform, // { tx, ty, scale, rot }
  transformMode,
  onTransformChange,
  frameWidth,
  frameHeight,
  // ms — when >0, the photo's opacity changes via CSS transition over
  // this duration. Used by creative mode's marc cross-fade so the onion
  // smoothly fades in/out around a marc swap. Default 0 (instant) —
  // author mode's slider needs to be responsive, not damped.
  opacityTransitionMs = 0,
}) {
  const dragState = React.useRef(null);
  const rootRef = React.useRef(null);

  // The Root element sits inside FitContainer's scaled Inner. Its visual
  // box is (frameWidth × S) × (frameHeight × S). We use rect.width/frameWidth
  // to recover S so viewport-pixel pointer deltas can be converted back to
  // virtual coords — otherwise drags get silently rescaled by viewport size.
  const measureFrame = () => {
    if (!rootRef.current) return { pageX: 0, pageY: 0, scale: 1 };
    const rect = rootRef.current.getBoundingClientRect();
    const scale = rect.width > 0 ? rect.width / frameWidth : 1;
    return { pageX: rect.left, pageY: rect.top, scale };
  };

  const beginDrag = (event, mode) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const frame = measureFrame();
    const centerX = frameWidth / 2 + transform.tx; // virtual
    const centerY = frameHeight / 2 + transform.ty; // virtual
    const centerPageX = frame.pageX + centerX * frame.scale;
    const centerPageY = frame.pageY + centerY * frame.scale;
    const startAngle = Math.atan2(
      event.clientY - centerPageY,
      event.clientX - centerPageX
    );
    dragState.current = {
      pointerId: event.pointerId,
      mode,
      startX: event.clientX,
      startY: event.clientY,
      startTransform: { ...transform },
      centerPageX,
      centerPageY,
      scale: frame.scale,
      lastAngle: startAngle,
      accumRotDeg: 0,
    };
  };

  const handleMove = (event) => {
    const s = dragState.current;
    if (!s || s.pointerId !== event.pointerId) return;
    const dxVirtual = (event.clientX - s.startX) / s.scale;
    const dyVirtual = (event.clientY - s.startY) / s.scale;

    if (s.mode === 'move') {
      onTransformChange({
        ...s.startTransform,
        tx: s.startTransform.tx + dxVirtual,
        ty: s.startTransform.ty + dyVirtual,
      });
      return;
    }

    const centerPageX = s.centerPageX;
    const centerPageY = s.centerPageY;

    if (s.mode === 'scale') {
      const initDist = Math.hypot(s.startX - centerPageX, s.startY - centerPageY) || 1;
      const curDist = Math.hypot(event.clientX - centerPageX, event.clientY - centerPageY);
      const ratio = curDist / initDist;
      const nextScale = clamp(s.startTransform.scale * ratio, 0.1, 6);
      onTransformChange({ ...s.startTransform, scale: nextScale });
      return;
    }

    if (s.mode === 'rotate') {
      const curAngle = Math.atan2(
        event.clientY - centerPageY,
        event.clientX - centerPageX
      );
      // Incremental delta — take the small step since last move and unwrap
      // across the atan2 ±π discontinuity. Accumulate to survive multi-turn
      // drags without jumps.
      let stepRad = curAngle - s.lastAngle;
      if (stepRad > Math.PI) stepRad -= 2 * Math.PI;
      else if (stepRad < -Math.PI) stepRad += 2 * Math.PI;
      s.lastAngle = curAngle;
      s.accumRotDeg += (stepRad * 180) / Math.PI;
      onTransformChange({
        ...s.startTransform,
        rot: s.startTransform.rot + s.accumRotDeg,
      });
      return;
    }
  };

  const endDrag = (event) => {
    const s = dragState.current;
    if (!s || s.pointerId !== event.pointerId) return;
    dragState.current = null;
  };

  if (!visible) return null;

  const transformCss = marcTransformToCss(transform);
  const marcOpacity = opacity ?? ONION_OPACITY;

  return (
    <Root ref={rootRef}>
      <MarcImg
        src={src}
        alt=""
        draggable={false}
        style={{
          transform: transformCss,
          opacity: marcOpacity,
          transition:
            opacityTransitionMs > 0
              ? `opacity ${opacityTransitionMs}ms ease-out`
              : "none",
        }}
        $interactive={transformMode}
        onPointerDown={transformMode ? (e) => beginDrag(e, 'move') : undefined}
        onPointerMove={transformMode ? handleMove : undefined}
        onPointerUp={transformMode ? endDrag : undefined}
        onPointerCancel={transformMode ? endDrag : undefined}
      />
      {transformMode && (
        <HandleLayer>
          {/* Scale handles at the four corners of the rendered image box.
              We overlay them using the same transform so they move with marc. */}
          <HandleWrap style={{ transform: transformCss }}>
            <CornerHandle
              style={{ left: 0, top: 0 }}
              onPointerDown={(e) => beginDrag(e, 'scale')}
              onPointerMove={handleMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            />
            <CornerHandle
              style={{ right: 0, top: 0 }}
              onPointerDown={(e) => beginDrag(e, 'scale')}
              onPointerMove={handleMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            />
            <CornerHandle
              style={{ left: 0, bottom: 0 }}
              onPointerDown={(e) => beginDrag(e, 'scale')}
              onPointerMove={handleMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            />
            <CornerHandle
              style={{ right: 0, bottom: 0 }}
              onPointerDown={(e) => beginDrag(e, 'scale')}
              onPointerMove={handleMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            />
            <RotateHandle
              style={{ left: '50%', top: -ROTATE_OFFSET }}
              onPointerDown={(e) => beginDrag(e, 'rotate')}
              onPointerMove={handleMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            />
          </HandleWrap>
        </HandleLayer>
      )}
    </Root>
  );
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

const Root = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const MarcImg = styled.img`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform-origin: center center;
  user-select: none;
  pointer-events: ${(p) => (p.$interactive ? 'auto' : 'none')};
  cursor: ${(p) => (p.$interactive ? 'grab' : 'default')};
  outline: ${(p) => (p.$interactive ? '2px dashed var(--color-warn)' : 'none')};
  outline-offset: 0px;
`;

const HandleLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const HandleWrap = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 100%;
  height: 100%;
  transform-origin: center center;
  pointer-events: none;
`;

const CornerHandle = styled.div`
  position: absolute;
  width: ${HANDLE_SIZE}px;
  height: ${HANDLE_SIZE}px;
  margin: -${HANDLE_SIZE / 2}px;
  background: var(--color-surface);
  border: 2px solid var(--color-warn);
  border-radius: 2px;
  pointer-events: auto;
  cursor: nwse-resize;
`;

const RotateHandle = styled.div`
  position: absolute;
  width: ${HANDLE_SIZE}px;
  height: ${HANDLE_SIZE}px;
  margin-left: -${HANDLE_SIZE / 2}px;
  background: var(--color-warn);
  border: 2px solid var(--color-selected-fg);
  border-radius: 50%;
  pointer-events: auto;
  cursor: grab;
`;

export default OnionMarc;
